// The play text, taken apart.
//
// **v1** (librarian's `analysis.py:_analyze_confidence_scores`) carries the conviction
// label and the pick only inside one string, "{label}: {pick}", with no structured
// field for either. Two surfaces read it: the slate-wide SuggestionsBox, which buckets
// by label, and each slate card, which prints the pick. They share this one parser so
// the two can't drift apart on what a label is.
//
// The four shapes v1 writes, from the live NFL slate of 2026-09-27:
//   spread     "Confident: Houston Texans -1.5"
//              "Confident: Denver Broncos +2.5 (upset potential)"
//   moneyline  "Confident: Detroit Lions ML"
//   total      "Moderate: Houston Texans vs Indianapolis Colts Over 42.5"
//   draw       "Moderate: Draw (Arsenal vs Chelsea)"
//
// **v2** (librarian B29, `util/llm/plays.py`) is a judged verdict, and its play says
// what it is: `pipeline_version: 2`, `label` (Lean, Play or Strong), `units` (½, 1 or 2),
// `best_bet`, and the play's own `line` and `price`. Its text keeps the same shape,
// "Play: Seattle Seahawks vs Denver Broncos Over 44". One fixture's plays are all one
// version: both writers replace the list whole.

import { isNum } from './format.js';

// Must match analysis.py:_get_conviction, strongest first.
export const CONVICTION_LEVELS = ['High conviction', 'Confident', 'Moderate', 'Marginal', 'Low conviction'];

// v2's scale (librarian `rubric.yaml` `scale`), strongest first.
export const TIERS = ['Strong', 'Play', 'Lean'];

export const isV2 = (play) => Boolean(play) && play.pipeline_version === 2;

export const getConviction = (text) => {
  if (typeof text !== 'string') return 'Other';
  for (const level of CONVICTION_LEVELS) {
    if (text.startsWith(level)) return level;
  }
  return 'Other';
};

// A pick coloured by how it settled. Pending is plain white, not grey: an
// unresolved pick is the normal state before kickoff, not a lesser one.
export const resultColor = (result) => {
  switch (result) {
    case 'hit': return '#4caf50';
    case 'loss': return '#FF6347';
    case 'push': return '#FFD700';
    default: return 'white';
  }
};

export const resultTitle = (result) => {
  switch (result) {
    case 'hit': return 'Hit';
    case 'loss': return 'Miss';
    case 'push': return 'Push';
    default: return 'Pending';
  }
};

// "½u", "1u", "2u"; '' when there's no stake to show.
export const unitsText = (units) => {
  if (!isNum(units)) return '';
  return units === 0.5 ? '½u' : `${units}u`;
};

// Profit per unit staked on a win at American `price`; even money when unknown.
const payout = (price) => {
  if (!isNum(price)) return 1;
  return price > 0 ? price / 100 : 100 / Math.abs(price);
};

// A graded v2 play's result in units, or null until it's graded. `settle` is +1, +½, 0,
// -½ or -1 per unit staked, before price: a win pays at the play's own price, and a loss
// costs the stake. The same arithmetic as librarian's `util/llm/evaluate.units_won`.
export const unitsWon = (play) => {
  if (!play || !isNum(play.settle)) return null;
  const units = isNum(play.units) && play.units ? play.units : 1;
  return units * (play.settle > 0 ? play.settle * payout(play.price) : play.settle);
};

// The total's "Favourite vs Underdog" and the draw's "(A vs B)" repeat the card's
// title, so the slate prints the rest.
const trim = (pick, betType, team) => {
  if (betType === 'total' && team && pick.startsWith(`${team} `)) return pick.slice(team.length + 1);
  if (betType === 'draw') return 'Draw';
  return pick;
};

// `{ label, pick, score, result, betType, version }`, plus `units` and `best` for v2, or
// null for a suggestion the slate can't print: a text that isn't a string, or a score
// that isn't a number (the same filter SuggestionsBox and Cards/suggestions.js apply).
// A text that isn't a string is the white-screen risk: everything here calls string
// methods on it, and nothing above it catches a throw.
//
// A v1 text with no recognised "{label}: " prefix is printed whole with no label.
// An unknown shape renders verbatim rather than being guessed at.
export const parsePick = (suggestion) => {
  if (!suggestion || typeof suggestion.text !== 'string' || !suggestion.text || !isNum(suggestion.score)) {
    return null;
  }
  const { text, score, result = null, bet_type: betType = null, team } = suggestion;

  if (isV2(suggestion)) {
    const label = TIERS.includes(suggestion.label) ? suggestion.label : null;
    const prefix = label ? `${label}: ` : null;
    const pick = prefix && text.startsWith(prefix) ? trim(text.slice(prefix.length), betType, team) : text;
    return {
      label, pick, score, result, betType, version: 2,
      units: isNum(suggestion.units) ? suggestion.units : null, best: suggestion.best_bet === true,
    };
  }

  const level = CONVICTION_LEVELS.find((l) => text.startsWith(`${l}: `));
  const label = level || null;
  const pick = level ? trim(text.slice(level.length + 2), betType, team) : text;
  return { label, pick, score, result, betType, version: 1 };
};

// v2's order: by tier, strongest first, then by score. v1 is by score.
const tierRank = (label) => {
  const i = TIERS.indexOf(label);
  return i === -1 ? TIERS.length : i;
};

export const comparePicks = (a, b) => {
  if (a.version !== b.version) return a.version === 2 ? -1 : 1;
  if (a.version === 2 && tierRank(a.label) !== tierRank(b.label)) return tierRank(a.label) - tierRank(b.label);
  return b.score - a.score;
};

// A fixture's printable picks, strongest first.
export const picksOf = (fixture) =>
  (Array.isArray(fixture?.suggestions) ? fixture.suggestions : [])
    .map(parsePick)
    .filter(Boolean)
    .sort(comparePicks);

// v1: "Houston Texans -1.5 · Confident · 0.68". v2: "Over 44 · Play 1u ★", with the star
// on the game's best bet and no raw score. A middle dot, not a hyphen: a hyphen beside
// a spread reads as a second minus sign.
export const formatPick = ({ pick, label, score, version, units, best }) => {
  if (version === 2) {
    const tier = [label, unitsText(units)].filter(Boolean).join(' ');
    return [pick, tier].filter(Boolean).join(' · ') + (best ? ' ★' : '');
  }
  return [pick, label, score.toFixed(2)].filter(Boolean).join(' · ');
};
